// mds-dojo — dijit/_onSelector — mds ADAPTATION SHIM for dojo/on's event delegation.
//
// The real dijit Menu/KeyNav family relies on `on.selector(matches, evtType)`
// (dojo/on) + dojo/mouse's bubbling `mouse.enter`/`mouse.leave`. The mds `dojo/on`
// intentionally has NO delegation (`on.selector`/`on.matches` are absent) and mds
// `dojo/mouse` maps enter/leave to the NON-bubbling native mouseenter/mouseleave.
// Also, mds `dojo/on(node, type, listener)` throws if `type` is a function, so
// extension events must be invoked directly. This module reproduces on.selector's
// semantics faithfully on top of the mds primitives so the ported source can call
// it where dojo wrote `on(node, on.selector(matches, evt), fn)`.
//
//   onSelector(node, matches, evtType, listener)
//       Delegate a BUBBLING dom event ("focusin"/"click"/"contextmenu"/"keydown")
//       or an extension event (dijit/a11yclick — a function). Reproduces
//       on.matches: walks up from event.target to `node`, calling `listener` with
//       `this` = the first ancestor matching `matches` (a function(node)->truthy,
//       or a CSS-selector string).
//
//   onSelectorEnter/onSelectorLeave(node, matches, listener)
//       Delegate mouse.enter/mouse.leave. Since native mouseenter/mouseleave don't
//       bubble (so can't be delegated on a container), these emulate the pair via
//       bubbling mouseover/mouseout with a relatedTarget descendant guard — exactly
//       how dojo/mouse's own enter/leave emulation distinguishes real crossings from
//       internal moves.
import on from '../dojo/on';
import dom from '../dojo/dom';

type Matcher = ((node: any) => any) | string;

function makeSelect(node: any, matches: Matcher): (eventTarget: any) => any {
  var matchFn: (n: any) => any = typeof matches === 'function'
    ? matches
    : function (n: any) { return n.nodeType === 1 && n.matches && n.matches(matches); };
  return function select(eventTarget: any): any {
    var n = eventTarget;
    if (n && n.nodeType !== 1) { n = n.parentNode; }
    while (n) {
      if (matchFn(n)) { return n; }
      if (n === node) { return null; }
      n = n.parentNode;
    }
    return null;
  };
}

export function onSelector(node: any, matches: Matcher, evtType: any, listener: any): any {
  var select = makeSelect(node, matches);
  function delegated(this: any, evt: any): any {
    var t = select(evt.target);
    if (t) {
      evt.selectorTarget = t;
      return listener.call(t, evt);
    }
  }
  if (typeof evtType === 'function') {
    // extension event (e.g. dijit/a11yclick): invoke directly (mds convention)
    return evtType(node, delegated);
  }
  return on(node, evtType, delegated);
}

function hover(node: any, matches: Matcher, domEvt: string, listener: any): any {
  var select = makeSelect(node, matches);
  return on(node, domEvt, function (evt: any) {
    var t = select(evt.target);
    // Only fire when the pointer actually crosses INTO/OUT OF the matched child,
    // not when moving between descendants of the same child.
    if (t && !(evt.relatedTarget && dom.isDescendant(evt.relatedTarget, t))) {
      listener.call(t, evt);
    }
  });
}

export function onSelectorEnter(node: any, matches: Matcher, listener: any): any {
  return hover(node, matches, 'mouseover', listener);
}

export function onSelectorLeave(node: any, matches: Matcher, listener: any): any {
  return hover(node, matches, 'mouseout', listener);
}
